import Vue from "vue";
import {
  AccountProductFeature,
  ProductStatus,
  Product,
  LoadingStatus,
  DisabledProducts,
  DisabledFeatures
} from "@/lib/utilities/enums";

function initialState() {
  return {
    ViewAccountProducts: [],
    UserAccountProducts: [],
    FeatureLoadingStatus: LoadingStatus.Loading,
    ProductAndFeaturesList: [],
    DealersPackages: []
  };
}

const getActiveFeatures = accountProducts => {
  if (accountProducts && accountProducts.length > 0) {
    let liveProducts = accountProducts.filter(
      vap => vap.ProductStatusID == ProductStatus.Live
    );
    let activeFeatures = [];
    liveProducts.forEach(lp => {
      const filteredFeatures = lp.Features
        ? lp.Features.filter(ft => ft.Active)
        : [];
      if (filteredFeatures.length > 0)
        activeFeatures = activeFeatures.concat(filteredFeatures);
    });

    return activeFeatures;
  }
  return [];
};

// Filter and return stores that have the requested product (and feature) enabled
const getStores = (storeFeatureList, storeList, [productID, featureID]) =>
  storeFeatureList
    .filter(
      vap =>
        vap &&
        vap.ProductID == productID &&
        (!featureID || (vap.Features || []).find(feat => feat && feat.Active && feat.FeatureID == featureID))
    )
    .map(vap => storeList.find(vss => vss && vss.AccountPlatformID == vap.AccountPlatformID))
    .filter(vss => vss)
    .sort((a, b) => {
      return a.Name && b.Name && a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1;
    });

const hasFeatureForSelectedStores = (features, storeIds) => {
  return (features || []).some(feature =>
    storeIds.includes(feature.AccountPlatformID)
  );
};

const state = initialState();

const getters = {
  ViewAccountProducts: function(state) {
    return state.ViewAccountProducts;
  },
  UserAccountProducts: function(state) {
    return state.UserAccountProducts;
  },
  DeskingStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.Desking]
    ),
  CXMStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.CXM]
    ),
  CalendarStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.Calendar]
    ),
  ProcessesStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.Processes]
    ),
  AMStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AlertMiner, AccountProductFeature.AlertMinerFull]
    ),
  SLMStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AlertMiner, AccountProductFeature.SLM]
    ),
  ILMStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.ILM]
    ),
  ExperienceManagerStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.ExperienceManager]
    ),
  InboxStores: (state, getters, rootState, rootGetters) =>
    getStores(getters.UserAccountProducts, rootGetters.User.AllowedStores, [
      Product.Pando,
      AccountProductFeature.Messaging
    ]),
  LiveSurveyProStores: (state, getters, rootState, rootGetters) =>
    getStores(getters.ViewAccountProducts, rootGetters.User.ViewSelectedStores, [
      Product.ReputationPro,
      AccountProductFeature.SurveyPro
    ]),
  AutoAssistantSalesStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AutoAssistant, AccountProductFeature.AutoAssistantSales]
    ),
  AutoAssistantServiceStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AutoAssistant, AccountProductFeature.AutoAssistantService]
    ),
  AutoAssistantLeadsStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AutoAssistant, AccountProductFeature.AutoAssistantLeads]
    ),
  GeoAlertStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.GeoAlert, AccountProductFeature.GeoAlert]
    ),
  CTCStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.CallTracking]
    ),
  AnalyticsStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.Analytics]
    ),
  ViewMessagingStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.Messaging]
    ),
  OneToOneStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.OneToOne, null]
    ),
  PALStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.OneToOne, AccountProductFeature.PAL]
    ),
  VideoStores: (state, getters, rootState, rootGetters) =>
    getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, AccountProductFeature.VideoMessaging]
    ),
  ProductAndFeaturesList: function(state) {
    return state.ProductAndFeaturesList;
  },
  DealersPackages: state => platformId => {
    if (state.DealersPackages && state.DealersPackages.length > 0) {
      let requestedDealerSettings = state.DealersPackages.find(ds => {
        return ds.AccountPlatformID == platformId;
      });
      if (requestedDealerSettings) {
        return requestedDealerSettings;
      }
    }
  },
  verifyAlertMinerPro: (state, getters) => platformId => {
    const hasCXM =
      getters.CXMStores &&
      getters.CXMStores.length > 0 &&
      getters.CXMStores.some(store => store.AccountPlatformID == platformId);
    const hasILM =
      getters.ILMStores &&
      getters.ILMStores.length > 0 &&
      getters.ILMStores.some(store => store.AccountPlatformID == platformId);
    // Product: A dealer with CXM and without ILM is considered an AlertMinerPro dealer
    return hasCXM && !hasILM;
  },
  ViewActiveFeatures(state) {
    return getActiveFeatures(state.ViewAccountProducts);
  },
  ViewActiveFeatureIds(state, getters) {
    return getters.ViewActiveFeatures.map(feature => feature.FeatureID);
  },
  ViewActiveFeatureFlags(state, getters, rootState, rootGetters) {
    const selectedStoreIds = rootGetters.User.ViewSelectedStores.map(
      str => str.AccountPlatformID
    );

    const activeFeatureFlags = {
      AlertMinerFull: getters.ViewActiveFeatureIds.includes(
        AccountProductFeature.AlertMinerFull
      ),
      CXM: getters.ViewActiveFeatureIds.includes(AccountProductFeature.CXM),
      ILM: getters.ViewActiveFeatureIds.includes(AccountProductFeature.ILM),
      Messages: getters.ViewActiveFeatureIds.includes(
        AccountProductFeature.Messaging
      )
    };
    return activeFeatureFlags;
  },
  ViewActiveFeatureStores(state, getters, rootState, rootGetters) {
    return {
      CXMStoreIds: getters.CXMStores.map(str => str.AccountPlatformID),
      ILMStoreIds: getters.ILMStores.map(str => str.AccountPlatformID)
    };
  }
};
const actions = {
  checkViewAccountFeatures(context) {
    let requestPackage = {};
    requestPackage.AccountID = context.getters.User.ViewAccountID;
    requestPackage.PlatformIds = context.getters.User.ViewSelectedStores.map(
      m => m.AccountPlatformID
    );
    requestPackage.ProductIds = [
      Product.Pando,
      Product.AlertMiner,
      Product.GeoAlert,
      Product.AutoAssistant,
      Product.OneToOne,
      Product.ReputationPro
    ];
    requestPackage.ProductStatus = [ProductStatus.Live];
    requestPackage.FeatureStatus = true;
    return context
      .dispatch("checkAccountFeatures", requestPackage)
      .then(function(data) {
        if (data && data.length > 0) {
          context.commit("updateViewAccountProducts", data);
        }
      });
  },
  checkUserAccountFeatures(context) {
    let requestPackage = {};
    requestPackage.AccountID = context.getters.User.AccountID;
    requestPackage.PlatformIds = context.getters.User.AllowedStores.map(
      m => m.AccountPlatformID
    );
    requestPackage.ProductIds = [Product.Pando, Product.ReputationPro];
    requestPackage.ProductStatus = [ProductStatus.Live];
    requestPackage.FeatureStatus = true;

    return context
      .dispatch("checkAccountFeatures", requestPackage)
      .then(function(data) {
        if (data && data.length > 0) {
          context.commit("updateUserAccountProducts", data);
        }
      });
  },
  checkAccountFeatures(context, requestPackage) {
    requestPackage.Size =
      requestPackage.PlatformIds.length * requestPackage.ProductIds.length;
    requestPackage.From = 0;
    return Vue.http.post("/products/", requestPackage).then(data => {
      if (data && data.data && data.data.Data.length > 0) {
        return data.data.Data;
      }
    });
  },
  loadProductListAndFeatures: function(context) {
    return new Promise((resolve, reject) => {
      Vue.http
        .get("products/getproductlist", true)
        .then(function(data) {
          if (data && data.data.Data) {
            context.commit("updateProductandFeaturesList", data.data.Data);
            resolve(data.data.Data);
          } else reject(data);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  getDealersPackages: function(context, platformId) {
    let request = {
      AccountPlatformId: platformId,
      PackageId: null
    };
    return new Promise((resolve, reject) => {
      Vue.http.post(`/packages/GetDealerPackage`, request).then(response => {
        if (response && response.data && response.data.Data) {
          context.commit("updateDealersPackages", response.data.Data);
          resolve(response.data.Data);
        }
      });
    });
  },
  fetchAllAccounts: function(context, request) {
    return Vue.http
      .get("platform/searchdealers?" + request)
      .then(function(data) {
        return data && data.data ? data.data.Data : [];
      });
  },
  fetchPlatforms: function(context, accountId) {
    return Vue.http
      .get("account/allplatforms/" + accountId)
      .then(function(data) {
        return data && data.data ? data.data.Data : [];
      });
  }
};
const mutations = {
  updateViewAccountProducts: function(state, data) {
    state.ViewAccountProducts = data;
  },
  updateUserAccountProducts: function(state, data) {
    state.UserAccountProducts = data;
  },
  featureDetailsLoaded: function(state) {
    state.FeatureLoadingStatus = LoadingStatus.Loaded;
  },
  updateProductandFeaturesList: function(state, data) {
    let products = [];
    data.forEach(p => {
      p.ProductFeatures.forEach(pf => {
        let product = {};

        product.productId = p.ProductID;
        product.productName = p.Name;

        product.featureId = pf.FeatureID;
        product.featureName = pf.FeatureName;
        if (
          !Object.values(DisabledFeatures).includes(product.featureId) &&
          !Object.values(DisabledProducts).includes(product.productId)
        ) {
          products.push(product);
        }
      });
    });
    state.ProductAndFeaturesList = products;
  },
  updateDealersPackages: function(state, data) {
    state.DealersPackages.push(data);
  }
};
export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/ProductFeatures.js